<template>
  <div class="space-root h-100">
    <b-spinner 
      v-if="isLoading" 
      class="d-block mx-auto text-primary my-2" 
    />
    <component
      v-else 
      :is="`layout-${layout}`"
      @error="handleLayoutError"
    >
      <router-view />
    </component>
  </div>
</template>

<script>
import Vue from 'vue';
import { ENABLED_APPS_ACTIONS } from '@/store/enabled-apps/enabled-apps-store-constants';
import { getImageResource } from '@/@core/utils/image-utils';
import { NOTIFICATIONS_ACTIONS } from '@/store/notifications/notifications-store-constants';
import FaviconPlaceholder from '@/assets/images/placeholders/light/nectios-favicon.png';
import Service from '@/config/service-identifiers';
import { selectLangFromCommunity } from '@/@core/utils/collective';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';
import { startNotifications, sendMixpanelNotification } from '@/@core/utils/notifications/notifications';
import { normalizeCommunitySlug, isValidCommunitySlug } from '@/@core/utils/community-utils';
import { useCommunityAnalytics } from '@/@core/composables/useCommunityAnalytics';
import { useCommunityChatbot } from '@/@core/composables/useCommunityChatbot';
import { trackPageview } from '@/utils/analytics';

import { mapGetters } from 'vuex';

// Vue Meta
import VueMeta from 'vue-meta';
Vue.use(VueMeta);

export default {
  name: 'SpaceRoot',

  metaInfo: {
    meta: [{
      vmid: 'google-site-verification',
      name: 'google-site-verification',
      content: '???',
    }]
  },

  components: {
    LayoutHorizontal: () => import('@/layouts/horizontal/LayoutHorizontal.vue' /* webpackChunkName: "LayoutHorizontal" */)
      .catch(error => {
        console.error('Failed to load LayoutHorizontal component:', error);
        return {
          template: '<div class="error-fallback p-3"><p>Failed to load layout. Please try refreshing the page.</p></div>'
        };
      }),
    LayoutVertical: () => import('@/layouts/vertical/LayoutVertical.vue' /* webpackChunkName: "LayoutVertical" */)
      .catch(error => {
        console.error('Failed to load LayoutVertical component:', error);
        return {
          template: '<div class="error-fallback p-3"><p>Failed to load layout. Please try refreshing the page.</p></div>'
        };
      }),
  },

  data() {
    return {
      isLoading: true,
      user: {},
    };
  },
  computed: {
    ...mapGetters([
      'loggedUser',
      'loggedMember',
      'apps',
      'communityinitialized',
      'collective',
      'mainCollective',
      'currentCollective',
      'collectiveFavicon',
      'integrations',
    ]),
    communitySlug() {
      const slug = this.$route.params.communityId;
      return normalizeCommunitySlug(slug);
    },
    showChatbot() {
      return this.mainSpace?.showChatbot;
    },
    chatbotCustom() {
      return this.mainSpace?.customChatbot;
    },
    documentTitle() {
      return this.mainSpace?.name;
    },
    faviconURL() {
      return getImageResource(this.collectiveFavicon) || FaviconPlaceholder;
    },
    mainSpace() {
      return this.mainCollective.key ? this.mainCollective : this.currentCollective;
    },
    isNexos() {
      try {
        return process.env.VUE_APP_CUSTOMER === 'nexos' || 
          (this.mainSpace && this.mainSpace.key === 'f3166db0-b635-11ef-805d-f79ce25412de');
      } catch(e) {
        console.warn('Error in isNexos computed property:', e);
        return false;
      }
    },
    layout() {
      try {
        if (this.isNexos) {
          return 'vertical';
        }
        if (this.$route.path.includes('/myself')) {
          return 'vertical';
        }
        if (this.currentCollective?.menu?.layout) {
          return this.currentCollective.menu.layout;
        }
        if (this.$store.state.appConfig?.layout?.type) {
          return this.$store.state.appConfig.layout.type;
        }
        return 'horizontal'; // Default fallback layout
      } catch(e) {
        console.warn('Error in layout computed property:', e);
        return 'horizontal'; // Safe fallback
      }
    },
  },

  metaInfo() {
    if(this.communitySlug === "terrassaindustria"){
      // <meta name="google-site-verification" content="" />
      return {
        meta: [{
          vmid: 'google-site-verification',
          name: 'google-site-verification',
          content: 'Rwg6wwWtBFZ8SIYUM3uDrzAkLc-W_hgukbI3ZEqGxCU',
        }]
      }
    }
  },

  async created() {
    // console.log('SpaceRoot created...');
    // Initialize composables
    this.analyticsComposable = useCommunityAnalytics();
    this.chatbotComposable = useCommunityChatbot();

    this.isLoading = true;

    // Validate and initialize
    if (!this.validateCommunitySlug()) {
      return;
    }

    // Load required data
    await this.ensureAppsLoaded();
    await this.ensureCommunityInitialized();
    await this.ensureCurrentCollectiveInitialized();

    // Check access permissions
    if (!this.checkMembershipAndAccess()) {
      this.isLoading = false;
      return;
    }

    // Finalize initialization
    this.isLoading = false;
    await this.initializeNotifications();
    this.setupRouterHooks();
    await this.$store.dispatch(NOTIFICATIONS_ACTIONS.initializeServiceWorker);
  },

  watch: {
    '$route'(to) {
      // Track pageview for nuestras-reglas-de-oro page
      if (to.path && to.path.includes('nuestras-reglas-de-oro')) {
        trackPageview('conocenos', '/nexos/conocenos/nuestras-reglas');
      }
    },
  },

  mounted() {
    //console.log('SpaceRoot mounted...');
    // Track pageview for nuestras-reglas-de-oro page on initial mount
    if (this.$route.path && this.$route.path.includes('nuestras-reglas-de-oro')) {
      trackPageview('conocenos', '/nexos/conocenos/nuestras-reglas');
    }
    
    if (!this.$store.state.copernicData.mountedSocket[this.communitySlug]) {
      this.$socket.emit('joinAnalytics', this.communitySlug);
      this.$socket.emit('joinCommunity', this.communitySlug);
      this.$store.state.copernicData.mountedSocket[this.communitySlug] = true;
    }
    if (!this.$store.state.copernicData.mountedSocket.global) {
      this.Notifications();
      this.$store.state.copernicData.mountedSocket.global = true;
    }
  },
  sockets: {
    connect(data) {
      this.$socket.emit('joinAnalytics', this.communitySlug);
      this.$socket.emit('joinCommunity', this.communitySlug);
    },
    notificationCount(params) {
      this.$store.dispatch(NOTIFICATIONS_ACTIONS.unreadCount, params);
    },
    newData(data) {
      if (data.communitySlug === this.communitySlug) {
        this.$store.commit('ADD_ITEM', data);
      }
    },
    updateData(data) {
      if (data.communitySlug === this.communitySlug) {
        this.$store.dispatch('UpdateFromSocket', data);
      }
    }
  },
  methods: {
    /**
     * Validates the community slug and redirects if invalid
     * @returns {boolean} True if valid, false if invalid (redirected)
     */
    validateCommunitySlug() {
      if (!isValidCommunitySlug(this.communitySlug)) {
        console.warn('SpaceRoot - Invalid community slug, redirecting to main root');
        this.$router.push('/');
        return false;
      }
      return true;
    },

    /**
     * Ensures apps are loaded for the community
     */
    async ensureAppsLoaded() {
      // console.log('ensureAppsLoaded...');
      if (typeof this.apps === 'object' && !this.apps.apps) {
        await this.$store.dispatch('getItems', {
          itemType: 'apps',
          page: 1,
          communitySlug: this.communitySlug || null,
        });
      }
    },

    /**
     * Ensures the community is initialized
     */
    async ensureCommunityInitialized() {
      // console.log('ensureCommunityInitialized...');
      const currentSpaceIsInitialized = this.communitySlug === this.communityinitialized;
      if (!this.communityinitialized || !currentSpaceIsInitialized) {
        await this.initCommunity(this.communitySlug);
      }
    },

    /**
     * Ensures the current collective is initialized
     */
    async ensureCurrentCollectiveInitialized() {
      // console.log('ensureCurrentCollectiveInitialized...');
      if (!this.currentCollective) {
        await this.$store.dispatch('initCurrentCommunity', {
          communitySlug: this.communitySlug,
        });
      }
    },

    /**
     * Checks membership and access permissions
     * Redirects to welcome page if user doesn't have access
     * @returns {boolean} True if user has access, false if redirected
     */
    checkMembershipAndAccess() {
      // console.log('checkMembershipAndAccess...');
      const routeName = this.$route.name;
      const collective = this.currentCollective;

      // Skip membership check for onboarding routes
      if (routeName === 'onboarding-form' || routeName === 'onboarding-plans') {
        return true;
      }

      // Sections can be accessed (TODO: Check parent membership)
      if (collective?.isSection) {
        return true;
      }

      // Check if user is a member with active status
      if (collective?.isMember && collective?.status === 3) {
        return true;
      }

      // Check if user is staff
      if (collective?.isStaff) {
        return true;
      }

      // User doesn't have access, redirect to welcome page
      this.$router.push({
        name: 'welcome',
        params: {
          communityId: collective?.slug,
        },
      });
      return false;
    },

    /**
     * Initializes notifications system
     */
    async initializeNotifications() {
      // console.log('initializeNotifications...');
      await startNotifications({
        memberKey: this.loggedMember?.key,
        communitySlug: this.communitySlug,
      });
      await sendMixpanelNotification('member-in-community');
    },

    /**
     * Sets up router hooks for analytics and language selection
     */
    setupRouterHooks() {
      // console.log('setupRouterHooks...');
      this.$router.afterEach((to, from) => {
        // Track page analytics
        this.$store.dispatch('addAnalytic', {
          morphType: 'page',
          action: 'enter-page',
          content: to.path,
        });

        // Update language from community
        if (this.mainSpace) {
          selectLangFromCommunity(this.mainSpace);
        }
      });
    },

    Notifications() {
      // do work
      /* this.$socket.emit("receiveNotifications", this.$route.params.communityId);

      //queue more work
      setTimeout(this.Notifications, 15000); */
    },
    /**
     * Initializes the community with all necessary setup
     * @param {string|object} communitySlug - The community slug to initialize
     */
    async initCommunity(communitySlug) {
      // console.log('initCommunity...');
      // Normalize and validate slug
      const normalizedSlug = normalizeCommunitySlug(communitySlug);
      if (!isValidCommunitySlug(normalizedSlug)) {
        console.error('Invalid community slug provided to initCommunity');
        this.$router.push({ name: 'error', params: { error: '404' } });
        return;
      }

      try {
        // Initialize current community
        await this.$store.dispatch('initCurrentCommunity', {
          communitySlug: normalizedSlug,
        });

        // Set language from community
        selectLangFromCommunity(this.mainSpace);

        // Update page title and meta tags
        this.updatePageMetadata();

        // Fetch enabled apps
        await this.$store.dispatch(ENABLED_APPS_ACTIONS.fetchEnabledApps);

        // Fetch and inject analytics
        await this.setupAnalytics();

        // Update head metadata
        this.updateHeadMetadata();

        // Setup chatbot
        this.setupChatbot();
      } catch (error) {
        console.error('Error initializing community:', error);
        this.$router.push({ name: 'error', params: { error: '404' } });
      }
    },

    /**
     * Updates page title and meta tags
     */
    updatePageMetadata() {
      console.log('updatePageMetadata...');
      const headMeta = this.mainSpace?.headMeta;
      this.$updateMetaTags({
        title: this.translate(this.documentTitle),
        favicon: this.faviconURL,
        description: headMeta?.name_description || '',
        keywords: headMeta?.name_keywords || '',
        googleSiteVerification: headMeta?.google_site_verification || '',
      });
    },

    /**
     * Fetches integrations and injects analytics scripts
     */
    async setupAnalytics() {
      // console.log('[SpaceRoot] setupAnalytics...');
      try {
        const integrations = await this.getIntegrations();
        // console.log('[SpaceRoot] Got integrations:', integrations);
        // console.log('[SpaceRoot] analyticsComposable exists:', !!this.analyticsComposable);
        // console.log('[SpaceRoot] analyticsComposable methods:', this.analyticsComposable ? Object.keys(this.analyticsComposable) : 'N/A');
        
        if (this.analyticsComposable && this.analyticsComposable.injectAnalytics) {
          // console.log('[SpaceRoot] Calling injectAnalytics...');
          this.analyticsComposable.injectAnalytics(integrations);
          // console.log('[SpaceRoot] injectAnalytics called successfully');
        } else {
          console.error('[SpaceRoot] analyticsComposable or injectAnalytics method not available!');
        }
      } catch (error) {
        console.error('[SpaceRoot] Error setting up analytics:', error);
        console.error('[SpaceRoot] Error stack:', error.stack);
        // Don't throw - analytics failures shouldn't break the app
      }
    },

    /**
     * Updates head metadata elements
     */
    updateHeadMetadata() {
      // console.log('updateHeadMetadata...');
      try {
        const localSpaceMetadata = this.currentCollective?.headMeta;
        const mainSpaceMetadata = this.mainCollective?.headMeta;
        const metadata = localSpaceMetadata ?? mainSpaceMetadata;

        if (!metadata) {
          return;
        }

        // Update meta tags safely
        const authorMeta = document.querySelector('meta[name="author"]');
        if (authorMeta && metadata.name_author) {
          authorMeta.setAttribute('content', metadata.name_author);
        }

        const descriptionMeta = document.querySelector('meta[name="description"]');
        if (descriptionMeta && metadata.name_description) {
          descriptionMeta.setAttribute('content', metadata.name_description);
        }

        const keywordsMeta = document.querySelector('meta[name="keywords"]');
        if (keywordsMeta && metadata.name_keywords) {
          keywordsMeta.setAttribute('content', metadata.name_keywords);
        }

        const twitterTitleMeta = document.querySelector('meta[name="twitter:title"]');
        if (twitterTitleMeta && metadata.name_twitter_title) {
          twitterTitleMeta.setAttribute('content', metadata.name_twitter_title);
        }

        const twitterDescriptionMeta = document.querySelector('meta[name="twitter:description"]');
        if (twitterDescriptionMeta && metadata.name_twitter_description) {
          twitterDescriptionMeta.setAttribute('content', metadata.name_twitter_description);
        }

        const ogTitleMeta = document.getElementById('ogtitle');
        if (ogTitleMeta && metadata.property_og_title) {
          ogTitleMeta.setAttribute('content', metadata.property_og_title);
        }

        const ogDescriptionMeta = document.getElementById('ogdescription');
        if (ogDescriptionMeta && metadata.property_og_description) {
          ogDescriptionMeta.setAttribute('content', metadata.property_og_description);
        }
      } catch (error) {
        console.error('Error updating head metadata:', error);
      }
    },

    /**
     * Sets up chatbot injection
     */
    setupChatbot() {
      try {
        if (this.chatbotComposable) {
          this.chatbotComposable.injectChatbot({
            showChatbot: this.showChatbot,
            customChatbot: this.chatbotCustom,
            isBackoffice: this.$route.path.includes('/backoffice'),
          });
        }
      } catch (error) {
        console.error('Error setting up chatbot:', error);
        // Don't throw - chatbot failures shouldn't break the app
      }
    },
    /**
     * Gets integrations from store or fetches them if not available
     * @returns {Promise<Array>} Array of integration objects
     */
    async getIntegrations() {
      // console.log('getIntegrations...');
      // Check if integrations are already cached in store
      if (this.integrations?.unpaginated?.length > 0) {
        return this.integrations.unpaginated;
      }
      return await this.fetchIntegrations();
    },

    /**
     * Fetches integrations from the backend API
     * @returns {Promise<Array>} Array of integration objects
     */
    async fetchIntegrations() {
      // console.log('[SpaceRoot] fetchIntegrations...');
      // console.log('[SpaceRoot] mainSpace:', this.mainSpace);
      // console.log('[SpaceRoot] mainSpace?.slug:', this.mainSpace?.slug);
      
      // Get the main space slug for fetching integrations
      const mainSpaceSlug = normalizeCommunitySlug(this.mainSpace?.slug);
      // console.log('[SpaceRoot] Normalized mainSpaceSlug:', mainSpaceSlug);
      
      if (!isValidCommunitySlug(mainSpaceSlug)) {
        console.warn('[SpaceRoot] fetchIntegrations - invalid mainSpaceSlug:', mainSpaceSlug);
        return [];
      }

      try {
        // console.log('[SpaceRoot] Fetching integrations from API with slug:', mainSpaceSlug);
        const response = await this.$store.$service[Service.BackendClient].get(
          'addons',
          {
            params: {
              type: 'integrations',
              communitySlug: mainSpaceSlug,
            },
          }
        );
        // console.log('[SpaceRoot] API response:', response);
        // console.log('[SpaceRoot] Response data:', response.data);
        // console.log('[SpaceRoot] Response data.data:', response.data?.data);
        const integrations = response.data?.data || [];
        // console.log('[SpaceRoot] Returning integrations:', integrations);
        return integrations;
      } catch (error) {
        console.error('[SpaceRoot] Error fetching integrations:', error);
        console.error('[SpaceRoot] Error details:', {
          message: error.message,
          response: error.response,
          stack: error.stack,
        });
        return [];
      }
    },
    translate(field) {
      return translateTranslationTable(this.$store.state.locale.currentLocale, field);
    },
    handleLayoutError(error) {
      console.error('Layout error:', error);
      this.$router.push({ name: 'error', params: { error: 'layout-error' } });
    },
  },
};
</script>
